/**
 * One chat message. Parses the content into segments (text / spot /
 * choices / hidden) and renders each appropriately. User messages are
 * plain text only; only assistant messages get parsed.
 *
 * Text segments are rendered as markdown — bold, italic, lists, links.
 * Links with the `poker-trainer://sheet/<id>` scheme open the study
 * sheet in a modal via the `onSheetLink` callback (mirrors iOS).
 */

import { parseSegments } from "@/features/chat/lib/messageSegments";
import type { Message, SpotAction, SpotPayload } from "@/shared/models";
import { ChoicesBlock } from "@/features/chat/components/ChoicesBlock";
import { SpotCard } from "@/features/chat/components/SpotCard";
import { VerdictProse } from "@/features/chat/components/VerdictProse";

interface MessageBubbleProps {
  message: Message;
  /** True when a later user message exists — disables spot/choice buttons. */
  locked: boolean;
  /** Callback receives the spot too so the chat layer can serialize the
   *  action with pot-fraction context — without that, "33% pot" presets
   *  reach the LLM as bare bb amounts and get mis-scored. */
  onSpotAction: (action: SpotAction, spot: SpotPayload) => void;
  onChoice: (option: string) => void;
  /** Called when the user taps a poker-trainer://sheet/<id> link in chat. */
  onSheetLink?: (sheetId: string) => void;
  /** Conversation id — SpotCard threads this into the
   *  POST /library-spots/report payload when a user flags a spot. */
  conversationId?: string;
  /** Suppress the LLM-emitted `choices` block. Used in Today's Drill,
   *  where the coach's default "Next spot" / "Walk through the reasoning"
   *  options don't fit the ladder (a daily "Next spot" would re-serve the
   *  same pinned spot). ChatView renders its own daily footer instead. */
  hideChoices?: boolean;
}

export function MessageBubble({
  message,
  locked,
  onSpotAction,
  onChoice,
  onSheetLink,
  conversationId,
  hideChoices = false,
}: MessageBubbleProps) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl bg-accent/20 text-text px-4 py-2.5 text-sm whitespace-pre-wrap">
          {message.content}
        </div>
      </div>
    );
  }

  const segments = parseSegments(message.content);
  // If streaming and we have no segments yet, show a pulsing placeholder
  if (segments.length === 0) {
    return (
      <div className="flex justify-start">
        <div className="rounded-2xl bg-surface px-4 py-2.5 text-sm text-text-muted">
          <span className="inline-block animate-pulse">…</span>
        </div>
      </div>
    );
  }

  // When the assistant message contains a spot, the bubble needs the full
  // chat-column width so the table can render at a usable size — otherwise
  // the bubble auto-shrinks to its narrowest text child and crushes the
  // table into a tiny oval. Detect this once and pick the wider layout.
  const hasSpot = segments.some((s) => s.kind === "spot");

  return (
    <div className="flex justify-start">
      <div
        className={[
          "space-y-2.5",
          hasSpot ? "w-full max-w-[680px]" : "max-w-[88%]",
        ].join(" ")}
      >
        {segments.map((segment, idx) => {
          switch (segment.kind) {
            case "text":
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-border/70 bg-surface text-text px-4 py-3 text-sm leading-relaxed prose-chat"
                >
                  <VerdictProse text={segment.text.trim()} onSheetLink={onSheetLink} />
                </div>
              );
            case "spot":
              return (
                <SpotCard
                  key={idx}
                  spot={segment.spot}
                  locked={locked}
                  onAction={(action) => onSpotAction(action, segment.spot)}
                  conversationId={conversationId}
                  // Post-report "Next spot" needs to be clearer than the
                  // canonical "Next spot" — when the LLM falls through
                  // to handle this (library miss), it would see an
                  // unactioned spot in history + "Next spot" and scold
                  // the user for skipping. The verbose phrasing tells
                  // the LLM what's going on. Backend heuristic
                  // recognizes this prefix as a spot-request turn.
                  onNextSpot={() =>
                    onChoice(
                      "Give me a new spot to drill. I'm passing on the previous one.",
                    )
                  }
                />
              );
            case "choices":
              if (hideChoices) return null;
              return (
                <ChoicesBlock
                  key={idx}
                  choices={segment.choices}
                  locked={locked}
                  onChoice={onChoice}
                />
              );
            case "hidden":
              return null;
          }
        })}
      </div>
    </div>
  );
}
